import type { InspirationIntentResult, IntentResult, SearchContext } from '@shared/types/types.ts';
import { isInspirationIntent } from '@shared/types/types.ts';
import { isProductItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { ComboContent, ComboItem, ComboKind, TileListItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';

/*                                                             */
export const COMBO_TILE_POSITION = 12;

const FASHION_ASSORTMENTS = new Set(['bekleidung', 'schuhe', 'wäsche']);
const HOME_ASSORTMENTS = new Set(['möbel', 'heimtextilien', 'dekoration']);

function toComboKind(searchContext?: SearchContext): ComboKind | undefined {
    const assortment = searchContext?.assortment?.toLowerCase();
    if (!assortment) return undefined;
    if (FASHION_ASSORTMENTS.has(assortment)) return 'outfit';
    if (HOME_ASSORTMENTS.has(assortment)) return 'home';
    return undefined;
}

export function isComboTileEligible(searchContext?: SearchContext): boolean {
    return toComboKind(searchContext) !== undefined;
}

/*                                             */
export function isComboGroupB(onex: OnEx | undefined): boolean {
    return onex?.isExperimentGroup('COMBO_TILE', 'E3966B') ?? false;
}

/*                                                                                              */
export function isComboTileRequested(onex: OnEx | undefined, isFirstPage: boolean): boolean {
    return isFirstPage && isComboGroupB(onex);
}

/**
 *
 *
 */
export function isComboSlotReserved(
    onex: OnEx | undefined,
    searchContext: SearchContext | undefined,
    currentPage: number,
): boolean {
    return currentPage === 1 && isComboGroupB(onex) && isComboTileEligible(searchContext);
}

/*                                                                              */
export function findComboItem(
    intents: IntentResult[],
    searchContext: SearchContext | undefined,
    onex: OnEx | undefined,
    currentPage: number,
): ComboContent | undefined {
    if (!isComboSlotReserved(onex, searchContext, currentPage)) return undefined;
    const kind = toComboKind(searchContext);
    const inspiration: InspirationIntentResult | undefined = intents.find(isInspirationIntent);
    if (!kind || !inspiration || inspiration.variationIds.length === 0) return undefined;
    return {
        type: 'Combo',
        comboId: inspiration.comboId,
        moodImage: inspiration.moodImage,
        variationIds: inspiration.variationIds,
        kind,
    };
}

/**
 *
 *
 */
function findReservedSlotIndex(items: TileListItem[]): number {
    return items.findIndex((item, index) => {
        const previous = items[index - 1];
        return (
            !!previous &&
            isProductItem(previous) &&
            isProductItem(item) &&
            item.localListPosition - previous.localListPosition > 1
        );
    });
}

/**
 *
 *
 *
 */
export function insertComboItem(items: TileListItem[], combo: ComboContent | undefined): TileListItem[] {
    if (!combo) return items;
    const index = findReservedSlotIndex(items);
    const previous = items[index - 1];
    if (index < 0 || !previous || !isProductItem(previous)) return items;
    const positioned: ComboItem = {
        ...combo,
        localListPosition: previous.localListPosition + 1,
        actualListPosition: previous.actualListPosition + 1,
    };
    return [...items.slice(0, index), positioned, ...items.slice(index)];
}
